import apiClient from './apiClient';

/**
 * E-mail delivery (v1.42.0). Every product e-mail is written to the outbox
 * and handed to the mail server by a worker that respects the provider's
 * sending limit (one.com: 25 per 5 minutes). These calls read that outbox so
 * the person who triggered a send can see whether it actually went out.
 *
 * All endpoints live under /admin/email and are open to admin AND support —
 * the API scopes a support user to the rows they requested themselves.
 * Rows never carry bodies (they can contain personal login links).
 */

/**
 * Helper function to handle errors — same envelope handling as adminService.
 * @param {object} error - Error object
 * @returns {Error} - Formatted error
 */
const handleError = (error) => {
  const message =
    error.response?.data?.error?.message ||
    error.response?.data?.message ||
    error.message;
  if (message) {
    const err = new Error(message);
    err.code = error.response?.data?.error?.code;
    err.details = error.response?.data?.error?.details || null;
    err.status = error.response?.status;
    return err;
  }
  return error;
};

/**
 * List outbox rows.
 * @param {object} params - { status?, kind?, batchId?, mine?, search?, page?, limit? }
 * @returns {Promise<{items: Array<object>, pagination: {total:number, page:number, limit:number, totalPages:number}}>}
 */
export const listOutbox = async (params = {}) => {
  try {
    const response = await apiClient.get('/admin/email/outbox', { params });
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * The figures above the table: what is waiting, what went out, and where the
 * rolling sending window stands right now.
 * @returns {Promise<{enabled:boolean, mailConfigured:boolean, rateLimit:{limit:number, windowMinutes:number, description:string},
 *   sentInWindow:number, capacity:number, windowResetsAt:string|null, cooldownUntil:string|null,
 *   queued:number, sent24h:number, failed7d:number, nextAttemptAt:string|null, estimatedMinutes:number}>}
 */
export const getOutboxSummary = async () => {
  try {
    const response = await apiClient.get('/admin/email/outbox/summary');
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Everything queued for one action (a Send Invitations click, one urgent
 * reminder) and whether all of it has settled. Polled by useEmailBatch.
 * @param {string} batchId
 * @returns {Promise<{batchId:string, items:Array<object>, counts:{total:number, queued:number, sent:number, failed:number, cancelled:number},
 *   complete:boolean, rateLimit:object, capacity:number, windowResetsAt:string|null, estimatedMinutes:number}>}
 */
export const getBatch = async (batchId) => {
  try {
    const response = await apiClient.get(`/admin/email/outbox/batch/${encodeURIComponent(batchId)}`);
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Put a failed (or cancelled) row back in the queue.
 * @param {number} id - outbox row id
 * @returns {Promise<{item: object}>}
 */
export const retryOutbox = async (id) => {
  try {
    const response = await apiClient.post(`/admin/email/outbox/${id}/retry`);
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Take a queued row out of the queue (it keeps its body, so it can be retried).
 * @param {number} id - outbox row id
 * @returns {Promise<{item: object}>}
 */
export const cancelOutbox = async (id) => {
  try {
    const response = await apiClient.post(`/admin/email/outbox/${id}/cancel`);
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

/**
 * Ask the delivery worker to run a tick now rather than at the next minute.
 * @returns {Promise<{scheduled:boolean, enabled:boolean}>}
 */
export const runOutbox = async () => {
  try {
    const response = await apiClient.post('/admin/email/outbox/run');
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

const emailService = {
  listOutbox,
  getOutboxSummary,
  getBatch,
  retryOutbox,
  cancelOutbox,
  runOutbox
};

export default emailService;
